import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Pressable, Text, View } from 'react-native';
import { FontAwesomeIcon } from '@rivallapp/volosports-components';
import { faPen } from '@fortawesome/pro-solid-svg-icons/faPen';
import { useQuery, useMutation } from '@apollo/client';

import { PillToggle } from '../PillToggle';
import { Button, Error, Portal } from '../../../components';
import { useAlertMessage } from '../../../hooks';
import {
  GROUP_QUERY,
  GROUP_UPDATE_LOCKED_MUTATION,
  GROUP_TOGGLE_AUTOLOCK_MUTATION,
  GROUP_AUTOLOCK_THRESHOLD_MUTATION,
} from './graphql';
import GroupUpdateTypesEnum from './group-update-types-enum';
import GroupUpdateConfirmation from './GroupUpdateConfirmation';

import styles from './styles';

const GroupLock: React.FC<{ groupId: string }> = ({ groupId }) => {
  const { showSuccess, showError } = useAlertMessage();

  const [selectedPopup, setSelectedPopup] = useState<GroupUpdateTypesEnum | null>(null);

  const [groupToggleLockedMutation] = useMutation(GROUP_UPDATE_LOCKED_MUTATION);
  const [groupToggleAutolockMutation] = useMutation(GROUP_TOGGLE_AUTOLOCK_MUTATION);
  const [groupAutolockThresholdMutation] = useMutation(GROUP_AUTOLOCK_THRESHOLD_MUTATION);

  const { data, loading, error, refetch } = useQuery(GROUP_QUERY, {
    // Force this re-query since groups can be disabled/enabled
    // from "Allowed Registrations" on the main league view
    fetchPolicy: 'cache-and-network',
    skip: !groupId,
    variables: { groupId },
  });

  const { group } = data || {};
  const { name, isLocked, autolockEnabled, autolockThreshold } = group || {};

  const toggleButtonText = isLocked ? 'Unlock' : 'Lock';

  const ThresholdElement = useCallback(
    () => (
      <Pressable
        style={styles.thresholdContainer}
        onPress={() => setSelectedPopup(GroupUpdateTypesEnum.Threshold)}
      >
        <Text style={[styles.nowrap, styles.marginX]}>{autolockThreshold}</Text>
        <FontAwesomeIcon icon={faPen} size="14px" />
      </Pressable>
    ),
    [autolockThreshold]
  );

  const onToggleLock = async () => {
    try {
      await groupToggleLockedMutation({
        variables: {
          input: {
            groupId,
            isLocked: !isLocked,
          },
        },
      });
      showSuccess(`${toggleButtonText}ed group ${name} successfully.`);
      await refetch();
      setSelectedPopup(null);
    } catch (e) {
      showError(e);
    }
  };

  const onToggleAutolock = async () => {
    try {
      await groupToggleAutolockMutation({
        variables: {
          input: {
            groupId,
            autolockEnabled: !autolockEnabled,
          },
        },
      });
      showSuccess(
        `Autolock ${!autolockEnabled ? 'en' : 'dis'}abled for group ${name} successfully.`
      );
      await refetch();
      setSelectedPopup(null);
    } catch (e) {
      showError(e);
    }
  };

  const onUpdateThreshold = async (updatedThreshold: string) => {
    try {
      await groupAutolockThresholdMutation({
        variables: {
          input: {
            groupId,
            autolockThreshold: Number.parseInt(updatedThreshold, 10),
          },
        },
      });
      showSuccess(`Autolock threshold updated for group ${name} successfully.`);
      await refetch();
      setSelectedPopup(null);
    } catch (e) {
      showError(e);
    }
  };

  const lockButtonPress = () => {
    const which = isLocked ? GroupUpdateTypesEnum.Unlock : GroupUpdateTypesEnum.Lock;
    setSelectedPopup(which);
  };

  const cancelAction = () => setSelectedPopup(null);

  const confirmAction = async (updatedValue: string) => {
    if (!selectedPopup) return;
    if (selectedPopup === GroupUpdateTypesEnum.Threshold) {
      await onUpdateThreshold(updatedValue);
    } else {
      await onToggleLock();
    }
  };

  if (error) return <Error error={String(error)} />;

  return (
    <View style={styles.row}>
      {!isLocked && (
        <PillToggle
          label="Auto Lock Threshold"
          value={!!autolockEnabled}
          onTogglePress={onToggleAutolock}
          optionalElement={autolockEnabled ? <ThresholdElement /> : null}
        />
      )}
      <View style={styles.button}>
        {/* @ts-ignore */}
        <Button bold small action onClick={lockButtonPress} disabled={loading}>
          {toggleButtonText}
        </Button>
      </View>

      {selectedPopup && (
        <Portal>
          <GroupUpdateConfirmation
            hidden={!selectedPopup}
            groupId={groupId}
            whichAction={selectedPopup}
            cancelAction={cancelAction}
            confirmAction={confirmAction}
          />
        </Portal>
      )}
    </View>
  );
};

GroupLock.propTypes = {
  groupId: PropTypes.string.isRequired,
};

export default GroupLock;
